import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  ArrowLeft, User, Camera, Lock, Shield, CheckCircle, 
  XCircle, Eye, EyeOff, AlertCircle
} from 'lucide-react';
import ProfilePhotoUpload from '../components/common/ProfilePhotoUpload';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function ProfileSettings() {
  const navigate = useNavigate();
  const { user, token, refreshUser } = useAuth();
  
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    phone: '',
    bio: ''
  });
  const [profileImage, setProfileImage] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Password change
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Verification status
  const [verificationStatus, setVerificationStatus] = useState(null);

  useEffect(() => {
    if (user) {
      setProfile({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        bio: user.bio || ''
      });
      setProfileImage(user.profile_image);
    }
    fetchVerificationStatus();
  }, [user]);

  const fetchVerificationStatus = async () => {
    try {
      const response = await axios.get(`${API}/verification/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVerificationStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch verification status:', error);
    }
  };

  const handleImageUpdate = (newUrl) => {
    setProfileImage(newUrl);
    refreshUser?.();
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/auth/profile`, profile, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Profile updated successfully!');
      refreshUser?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordForm.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Password changed successfully!');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-stone-200">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center gap-4">
          <button 
            onClick={() => navigate(-1)}
            className="w-10 h-10 rounded-full hover:bg-stone-100 flex items-center justify-center"
          >
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </button>
          <h1 className="text-lg font-semibold text-slate-900">Profile Settings</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-8">
        {/* Profile Image Section */}
        <section className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900 mb-6 flex items-center gap-2">
            <Camera className="w-5 h-5 text-amber-500" />
            Profile Photo
          </h2>
          
          <div className="flex items-center gap-6">
            <ProfilePhotoUpload
              currentImage={profileImage}
              userName={user?.name}
              onImageUpdate={handleImageUpdate}
              size="lg"
            />
            
            <div>
              <p className="font-medium text-slate-900">{user?.name}</p>
              <p className="text-sm text-slate-500">{user?.email}</p>
              <p className="text-xs text-slate-400 mt-2">Click photo to take a selfie or upload</p>
            </div>
          </div>
        </section>

        {/* Basic Info Section */}
        <section className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900 mb-6 flex items-center gap-2">
            <User className="w-5 h-5 text-amber-500" />
            Basic Information
          </h2>
          
          <form onSubmit={handleSaveProfile} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Full Name</Label>
                <Input
                  value={profile.name}
                  onChange={(e) => setProfile(prev => ({ ...prev, name: e.target.value }))}
                  className="h-12 mt-1.5"
                  data-testid="profile-name"
                />
              </div>
              <div>
                <Label>Phone Number</Label>
                <Input
                  value={profile.phone}
                  onChange={(e) => setProfile(prev => ({ ...prev, phone: e.target.value }))}
                  className="h-12 mt-1.5"
                  placeholder="+63 9XX XXX XXXX"
                  data-testid="profile-phone"
                />
              </div>
            </div>
            
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={profile.email}
                onChange={(e) => setProfile(prev => ({ ...prev, email: e.target.value }))}
                className="h-12 mt-1.5"
                data-testid="profile-email"
              />
            </div>
            
            <div>
              <Label>Bio</Label>
              <textarea
                value={profile.bio}
                onChange={(e) => setProfile(prev => ({ ...prev, bio: e.target.value }))}
                className="w-full h-24 mt-1.5 border border-stone-200 rounded-lg px-3 py-2 resize-none"
                placeholder="Tell us a bit about yourself..."
                data-testid="profile-bio"
              />
            </div>
            
            <Button 
              type="submit" 
              disabled={saving}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="save-profile-btn"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </form>
        </section>

        {/* Password Section */}
        <section className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900 mb-6 flex items-center gap-2">
            <Lock className="w-5 h-5 text-amber-500" />
            Change Password
          </h2>
          
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <Label>Current Password</Label>
              <div className="relative mt-1.5">
                <Input
                  type={showPasswords.current ? 'text' : 'password'}
                  value={passwordForm.current_password}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, current_password: e.target.value }))}
                  className="h-12 pr-10"
                  data-testid="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPasswords.current ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>New Password</Label>
                <div className="relative mt-1.5">
                  <Input
                    type={showPasswords.new ? 'text' : 'password'}
                    value={passwordForm.new_password}
                    onChange={(e) => setPasswordForm(prev => ({ ...prev, new_password: e.target.value }))}
                    className="h-12 pr-10"
                    data-testid="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPasswords.new ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <div>
                <Label>Confirm New Password</Label>
                <div className="relative mt-1.5">
                  <Input
                    type={showPasswords.confirm ? 'text' : 'password'}
                    value={passwordForm.confirm_password}
                    onChange={(e) => setPasswordForm(prev => ({ ...prev, confirm_password: e.target.value }))}
                    className="h-12 pr-10"
                    data-testid="confirm-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPasswords.confirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
            </div>
            
            <Button 
              type="submit" 
              disabled={changingPassword || !passwordForm.current_password || !passwordForm.new_password}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="change-password-btn"
            >
              {changingPassword ? 'Changing...' : 'Change Password'}
            </Button>
          </form>
        </section>

        {/* Face Verification Section */}
        <section className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900 mb-6 flex items-center gap-2">
            <Shield className="w-5 h-5 text-amber-500" />
            Identity Verification
          </h2>
          
          {verificationStatus?.face_verified ? (
            <div className="flex items-center gap-4 p-4 bg-green-50 rounded-xl">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="font-medium text-green-800">Identity Verified</p>
                <p className="text-sm text-green-600">
                  Verified on {new Date(verificationStatus.face_verification_date).toLocaleDateString()}
                  {verificationStatus.face_similarity_score && (
                    <span className="ml-2">• {verificationStatus.face_similarity_score.toFixed(1)}% match</span>
                  )}
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-start gap-4 p-4 bg-amber-50 rounded-xl">
                <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800">Verify Your Identity</p>
                  <p className="text-sm text-amber-700 mt-1">
                    Complete ID verification to increase trust and unlock all features. 
                    Just upload your government ID and we'll verify your name automatically.
                  </p>
                </div>
              </div>
              
              <Button 
                onClick={() => navigate('/verification')}
                className="bg-amber-500 hover:bg-amber-600 text-slate-900"
                data-testid="start-verification-btn"
              >
                <Shield className="w-4 h-4 mr-2" />
                Start Verification
              </Button>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
